/**
 * Configuración: factores del motor, catálogo e insumos.
 *
 * Todo esto vive en los DATOS, no en el código. Los costos, los tiempos y el gasto
 * fijo cambian con el tiempo —sube el cobre, cambia el sueldo de la cuadrilla, se
 * mide el gasto real de operar— y el sistema tiene que absorberlo sin tocar una
 * línea. Cada cambio aquí recalcula todas las cotizaciones al instante.
 *
 * `factor` merece explicación porque es el menos obvio: es la porción del gasto fijo
 * diario que absorbe cada producto. Si el gasto fijo son 2,800 al día y un producto
 * tiene factor 0.40, ese producto carga 1,120 de gasto de operación. Es lo que hace
 * que el UAFIR refleje el costo real de tener el negocio abierto, no solo el material.
 */

import { useEffect, useState } from 'react'
import * as store from '../datos/store.js'
import Despiece from './Despiece.jsx'
import { calcularFabricacion, tieneDespiece } from '../motor/fabricacion.js'
import { TALLER_BASE } from '../motor/taller.js'
import { usuario, salir } from '../firebase.js'

const mx = n => '$' + Math.round(n || 0).toLocaleString('es-MX')

/** Descripciones en español de cada factor, para no editar a ciegas. */
/* El nombre que SE LEE es español de negocio (crítica 2026-08-20: «gastosAdminPct»
   es voz de código, no de dueño); la clave técnica se conserva visible en la ayuda,
   porque es con ella que se audita el motor. */
const NOMBRE_FACTOR = {
  IVA: 'IVA',
  TDC: 'Tipo de cambio',
  UtilidadComplementos: 'Margen en complementos',
  UtilidadConsumibles: 'Margen en consumibles',
  UtilidadManoDeObra: 'Margen en mano de obra',
  UtilidadTraslado: 'Margen en traslado',
  UtilidadPeajesYHospedajes: 'Margen en casetas y hospedaje',
  minutoManoDeObra: 'Costo del minuto de cuadrilla',
  MinutosDia: 'Minutos productivos por jornada',
  CostoTDC: 'Comisión de la terminal bancaria',
  CostoDiaMetropolitano: 'Gasto fijo diario',
  gastosAdminPct: 'Gastos de administración',
  comisionVentaPct: 'Comisión de venta',
  CostoGasolina: 'Litro de gasolina',
  RendimientoGasolina: 'Rendimiento del vehículo',
  CostoDesgasteTransporte: 'Desgaste del vehículo',
  CostoMinutoTraslado: 'Costo del minuto al volante'
}

const AYUDA = {
  IVA: 'Multiplicador de IVA. 1.16 = 16%',
  TDC: 'Tipo de cambio, si algún insumo se cotiza en otra moneda. 1 = todo en pesos',
  UtilidadComplementos: 'Margen sobre complementos. 1.45 = 45% encima del costo',
  UtilidadConsumibles: 'Margen sobre consumibles',
  UtilidadManoDeObra: 'Margen sobre la mano de obra',
  UtilidadTraslado: 'Margen sobre el traslado',
  UtilidadPeajesYHospedajes: 'Margen sobre casetas y hospedaje',
  minutoManoDeObra: 'Costo real del minuto de cuadrilla, en pesos',
  MinutosDia: 'Minutos productivos por jornada. De aquí salen los días de obra',
  CostoTDC: 'Comisión de la terminal bancaria. 0.036 = 3.6%',
  CostoDiaMetropolitano: 'Gasto fijo diario del negocio, que se prorratea entre los productos según su factor',
  gastosAdminPct: 'Gastos de administración, como % del gasto de operación',
  comisionVentaPct: 'Comisión de venta, como % del precio',
  CostoGasolina: 'Precio del litro de gasolina',
  RendimientoGasolina: 'Kilómetros por litro del vehículo',
  CostoDesgasteTransporte: 'Desgaste del vehículo por kilómetro',
  CostoMinutoTraslado: 'Costo del minuto al volante'
}

/** Campo de texto que guarda al salir, hermano del Numero de abajo. */
function Texto({ etiqueta, valor, onGuardar }) {
  return (
    <label className="f">
      <span>{etiqueta}</span>
      <input defaultValue={valor || ''}
        onBlur={e => { const v = e.target.value.trim(); if (v !== (valor || '')) onGuardar(v) }} />
    </label>
  )
}

/** Renglón de alta de una herramienta nueva: con el nombre basta. */
function AltaHerramienta({ onCrear }) {
  const [nombre, setNombre] = useState('')
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
      <label className="f grow" style={{ margin: 0 }}>
        <span>Herramienta nueva</span>
        <input value={nombre} placeholder="Megóhmetro, taladro, pinza de corte…"
          onChange={e => setNombre(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && nombre.trim()) { onCrear(nombre.trim()); setNombre('') }
          }} />
      </label>
      <button className="ib pri" disabled={!nombre.trim()}
        onClick={() => { onCrear(nombre.trim()); setNombre('') }}>Agregar</button>
    </div>
  )
}

/**
 * Las herramientas que declara un producto. Es la pieza que «hace evidente» la que
 * falta: si al dar de alta el producto no está la herramienta en la lista, se
 * agrega al vuelo aquí mismo, sin ir a otra pestaña.
 */
function SelectorHerramientas({ seleccion = [], herramientas, onCambiar }) {
  const [nueva, setNueva] = useState('')
  const activas = herramientas.filter(h => h.activa !== false)
  const alternar = (id, dentro) =>
    onCambiar(dentro ? [...seleccion, id] : seleccion.filter(x => x !== id))
  const crear = async () => {
    if (!nueva.trim()) return
    const id = await store.guardar('catalogoHerramientas',
      { nombre: nueva.trim(), descripcion: '', activa: true })
    onCambiar([...seleccion, id])
    setNueva('')
  }
  return (
    <div style={{ marginTop: 10 }}>
      <div className="s" style={{ marginBottom: 6 }}>
        <b>Herramientas que pide este producto</b> — salen en la orden de servicio y
        en la salida de bodega.
      </div>
      {activas.map(h => (
        <label key={h.id} className="f"
          style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 4px' }}>
          <input type="checkbox" checked={seleccion.includes(h.id)} style={{ width: 20, height: 20 }}
            onChange={e => alternar(h.id, e.target.checked)} />
          <span style={{ margin: 0 }}>{h.nombre}</span>
        </label>
      ))}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
        <input value={nueva} placeholder="¿Falta una? Agrégala aquí mismo"
          onChange={e => setNueva(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') crear() }} />
        <button className="ib sm" disabled={!nueva.trim()} onClick={crear}>Agregar</button>
      </div>
    </div>
  )
}

function Numero({ etiqueta, ayuda, valor, onGuardar, paso = 0.01, provisional }) {
  return (
    <label className="f">
      <span>{etiqueta}{provisional && <span className="sello">Sin calibrar</span>}</span>
      <input type="number" step={paso} defaultValue={valor}
        onBlur={e => {
          const v = parseFloat(e.target.value)
          if (!Number.isNaN(v) && v !== valor) onGuardar(v)
        }} />
      {ayuda && <span style={{ marginTop: 4 }}>{ayuda}</span>}
    </label>
  )
}

/* Las ocho secciones, partidas en dos módulos (bloque 4, 2026-08-18): el CATÁLOGO
   es dar de alta lo que se vende; el NEGOCIO son los supuestos que mueven precios.
   Sin `grupo`, se muestran todas — es el modo de desarrollo. */
const SECCIONES = ['Factores', 'Productos', 'Insumos', 'Canales', 'Fabricación', 'Obra', 'Taller', 'Herramientas', 'SYSCOM', 'Protocolos']
const GRUPOS = {
  catalogo: [1, 2, 4, 7, 8, 9],
  negocio: [0, 3, 5, 6]
}

export default function Configuracion({ factores, productos, dispositivos, equipos, categorias, ctx,
                                        preferencias = {}, taller = {}, despieces = {}, onGuardarDespiece,
                                        herramientas = [], protocolos = [], grupo }) {
  const visibles = GRUPOS[grupo] || SECCIONES.map((_, i) => i)
  const [seccion, setSeccion] = useState(visibles[0])
  const [piezaAbierta, setPiezaAbierta] = useState(null)

  /* EDICIÓN DIFERIDA DE FACTORES (Regla 5 de la teoría, crítica 2026-08-20):
     cada factor recalcula TODAS las cotizaciones —incluidas las firmadas en
     borrador— y antes se guardaba al salir de cada campo: un dedo equivocado
     movía el negocio entero sin alto de por medio. Ahora los cambios se acumulan
     en un borrador local y solo «Aplicar» los suelta, todos juntos; «Descartar»
     regresa los campos a como estaban. */
  const [borradorFactores, setBorradorFactores] = useState({})
  const [versionFactores, setVersionFactores] = useState(0)   // remonta los campos al descartar
  const cambiarFactor = (llave, valor) => setBorradorFactores(prev => {
    const sig = { ...prev }
    if (valor === factores[llave]) delete sig[llave]
    else sig[llave] = valor
    return sig
  })
  const pendientes = Object.keys(borradorFactores)
  const aplicarFactores = async () => {
    await store.guardar('config', { ...factores, ...borradorFactores, id: 'factores' })
    setBorradorFactores({})
  }
  const descartarFactores = () => { setBorradorFactores({}); setVersionFactores(v => v + 1) }

  /* Las preferencias viven en el mismo documento de configuración que los factores,
     pero separadas: los factores son economía del negocio y éstas son criterio de obra. */
  /* La configuracion VIVA del taller. Hasta hoy se pasaba `{}` y los valores eran
     constantes en la practica, pese a que el comentario del motor prometia lo contrario. */
  const cfgTaller = { ...TALLER_BASE, ...taller }

  const guardarPreferencia = async (llave, valor) =>
    await store.guardar('config', { ...preferencias, id: 'preferencias', [llave]: valor })

  const guardarProducto = async (sku, cambios) => {
    const p = productos.find(x => x.sku === sku)
    if (p) await store.guardar('catalogoProductos', { ...p, ...cambios })
  }
  const guardarInsumo = async (sku, cambios) => {
    const d = dispositivos.find(x => x.sku === sku)
    if (d) await store.guardar('catalogoDispositivos', { ...d, ...cambios })
  }

  return (
    <>
      <div className="tabs" style={{ flexWrap: 'wrap' }}>
        {visibles.map(i => (
          <button key={i} className={`tb ${seccion === i ? 'on' : ''}`}
            style={{ flex: '1 1 30%' }}
            onClick={() => { setSeccion(i); setPiezaAbierta(null) }}>{SECCIONES[i]}</button>
        ))}
      </div>

      {seccion === 0 && (
        <>
          <div className="nota">
            Estos números recalculan <b>todas</b> las cotizaciones, incluidas las ya
            guardadas. Por eso aquí nada se guarda solo: edita con calma y aplica los
            cambios juntos al final — o descártalos.
          </div>
          <div className="warn">
            <b>Sin calibrar:</b> el gasto fijo diario (<code>CostoDiaMetropolitano</code>) todavía
            no sale de la operación real. Gobierna el costo de operación y, con él, el ajuste
            automático de precio: mientras no se mida, la protección puede estar inflando o
            hundiendo el precio de cada cotización.
          </div>
          <div className="card">
            {Object.keys(AYUDA).map(k => (
              <Numero key={`${versionFactores}-${k}`} etiqueta={NOMBRE_FACTOR[k] || k}
                ayuda={`${AYUDA[k]} · Clave: ${k}`}
                valor={borradorFactores[k] ?? factores[k]}
                paso={k === 'CostoDiaMetropolitano' || k === 'MinutosDia' ? 10 : 0.01}
                provisional={k === 'CostoDiaMetropolitano'}
                onGuardar={v => cambiarFactor(k, v)} />
            ))}
          </div>
          {pendientes.length > 0 && (
            <div className="nota acento">
              <b>{pendientes.length === 1 ? 'Un cambio sin aplicar' : `${pendientes.length} cambios sin aplicar`}:</b>{' '}
              {pendientes.map(k => `${NOMBRE_FACTOR[k] || k}: ${factores[k]} → ${borradorFactores[k]}`).join(' · ')}.
              Al aplicar, toda cotización guardada se recalcula con los valores nuevos.
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" className="ib" onClick={descartarFactores}>Descartar</button>
                <button type="button" className="ib pri" onClick={aplicarFactores}>
                  Aplicar a todas las cotizaciones
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {seccion === 1 && (
        <>
          <div className="warn">
            El <b>factor</b> es la porción del gasto fijo diario que carga cada producto.
            Hoy son valores provisionales: se calibran con la operación real.
          </div>
          {categorias.map(c => {
            const lista = productos.filter(p => p.cat === c.id)
            if (!lista.length) return null
            return (
              <div key={c.id}>
                <div className="sec">{c.nombre}</div>
                {lista.map(p => (
                  <details className="card" key={p.sku}>
                    <summary style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                      {p.img ? <img className="ph xs" alt="" src={p.img} /> : <span className="ph xs" />}
                      <span className="grow">
                        <span className="t">{p.nombre}</span>
                        <span className="s" style={{ display: 'block' }}>
                          {p.sku} · {p.tIns + (p.tCfg || 0)} min · factor {p.factor}
                        </span>
                      </span>
                    </summary>
                    <div style={{ marginTop: 12 }}>
                      <div className="two">
                        <Numero etiqueta="Minutos de instalación" valor={p.tIns} paso={1}
                          onGuardar={v => guardarProducto(p.sku, { tIns: v })} />
                        <Numero etiqueta="Minutos de configuración" valor={p.tCfg || 0} paso={1}
                          onGuardar={v => guardarProducto(p.sku, { tCfg: v })} />
                      </div>
                      <div className="two">
                        <Numero etiqueta="Precio fijo (0 = se calcula)" valor={p.precio || 0} paso={50}
                          onGuardar={v => guardarProducto(p.sku, { precio: v })} />
                        <Numero etiqueta="Factor de gasto fijo" valor={p.factor || 0} paso={0.01}
                          provisional onGuardar={v => guardarProducto(p.sku, { factor: v })} />
                      </div>
                      <div className="s">
                        Insumos: {(p.comp || []).map(i => `${i.sku} ×${i.c}`).join(' · ') || 'ninguno'}
                      </div>
                      <SelectorHerramientas seleccion={p.herr || []} herramientas={herramientas}
                        onCambiar={ids => guardarProducto(p.sku, { herr: ids })} />
                      <TextoLargo etiqueta="Qué le dice al cliente — sale impreso bajo su renglón en la propuesta"
                        valor={p.explica}
                        onGuardar={v => guardarProducto(p.sku, { explica: v })} />
                      <label className="ib" style={{ marginTop: 10, display: 'inline-flex',
                        alignItems: 'center', cursor: 'pointer' }}>
                        Cambiar foto
                        <input type="file" accept="image/*" hidden onChange={async e => {
                          const f = e.target.files?.[0]; if (!f) return
                          const chica = await store.comprimirImagen(f)
                          await guardarProducto(p.sku, { img: chica })
                          e.target.value = ''
                        }} />
                      </label>
                    </div>
                  </details>
                ))}
              </div>
            )
          })}
        </>
      )}

      {seccion === 2 && (
        <>
          <div className="nota">
            El <b>tipo</b> decide la rama de cálculo: los consumibles llevan su propia utilidad,
            todo lo demás va como complemento. Marca «IVA incluido» cuando el precio del proveedor
            ya lo traiga, como los de Syscom.
          </div>
          {/* El enganche técnico se muestra porque cambia lo que el insumo puede hacer:
              uno con `tec` lo encuentran solos los levantamientos de riego y eléctrico;
              uno sin `tec` solo entra a una cotización si alguien lo pone a mano. */}
          <div className="nota">
            Los insumos marcados <b className="pastilla">automático</b> los encuentran solos
            los levantamientos: si das de alta un cable del 12 más barato, la siguiente
            cotización lo usa sin que toques nada. Los demás hay que agregarlos a mano.
          </div>
          {dispositivos.map(d => (
            <div className="card" key={d.sku}>
              <div className="t">{d.nombre}</div>
              <div className="s" style={{ marginBottom: 10 }}>
                {d.sku}
                {d.tec && <span className="pastilla" style={{ marginLeft: 8 }}>
                  automático · {descripcionTec(d.tec)}
                </span>}
              </div>
              <div className="two">
                <Numero etiqueta="Costo" valor={d.costo} paso={0.5}
                  onGuardar={v => guardarInsumo(d.sku, { costo: v })} />
                <label className="f"><span>Tipo</span>
                  <select defaultValue={d.tipo}
                    onChange={e => guardarInsumo(d.sku, { tipo: e.target.value })}>
                    <option value="Consumibles">Consumibles</option>
                    <option value="Complementos">Complementos</option>
                  </select>
                </label>
              </div>
              <label className="f" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                <input type="checkbox" defaultChecked={!!d.iva} style={{ width: 20, height: 20 }}
                  onChange={e => guardarInsumo(d.sku, { iva: e.target.checked })} />
                <span style={{ margin: 0 }}>El costo ya incluye IVA</span>
              </label>
            </div>
          ))}
        </>
      )}

      {seccion === 3 && (
        <>
          <div className="nota">
            Un canal define la logística base de la cotización y la comisión que se reparte.
          </div>
          {equipos.map(eq => (
            <div className="card" key={eq.id}>
              <div className="t">{eq.nombre}</div>
              <div className="s" style={{ marginBottom: 10 }}>
                {eq.distancia} km · {eq.tiempo} min · comisión {eq.proporcionDeComision}%
                {eq.distribuidor ? ' · distribuidor' : ''}
              </div>
              <div className="two">
                <Numero etiqueta="Distancia base (km)" valor={eq.distancia} paso={1}
                  onGuardar={v => store.guardar('config', {
                    id: 'equipos',
                    lista: equipos.map(x => x.id === eq.id ? { ...x, distancia: v } : x)
                  })} />
                <Numero etiqueta="Comisión (%)" valor={eq.proporcionDeComision} paso={0.5}
                  onGuardar={v => store.guardar('config', {
                    id: 'equipos',
                    lista: equipos.map(x => x.id === eq.id ? { ...x, proporcionDeComision: v } : x)
                  })} />
              </div>
            </div>
          ))}
        </>
      )}

      {seccion === 4 && (
        <PanelFabricacion productos={productos} despieces={despieces}
          cfgTaller={cfgTaller} onGuardarDespiece={onGuardarDespiece} />
      )}

      {/* ---- Criterios de obra ----
          Lo que vive aquí NO es norma: son las decisiones de Juan que cambian un cálculo.
          La ampacidad de un 12 AWG o el factor de relleno de un tubo no están en esta
          pantalla y no deben estarlo — son ley, y editarlos sería fabricar un dato. */}
      {seccion === 5 && (
        <>
          <div className="nota">
            Estos son <b>criterios tuyos</b>, no de la norma, y por eso se pueden mover: qué
            cable compras, cuánto desperdicia tu cuadrilla, cuánta reserva dejas en el
            tablero. Lo que la norma manda —ampacidad, caída de tensión, factores de
            demanda— no aparece aquí a propósito: no es negociable.
          </div>

          <div className="card">
            <label className="f">
              <span>Tipo de aislamiento del conductor</span>
              <select defaultValue={preferencias.aislamiento || 'thw'}
                onChange={e => guardarPreferencia('aislamiento', e.target.value)}>
                <option value="thw">THW</option>
                <option value="thhn">THHN</option>
              </select>
              <span style={{ marginTop: 4 }}>
                No cambia solo el precio: <b>cambia el diámetro del tubo</b>. Un cable del 12
                en THW ocupa 22.77 mm² y en THHN ocupa 8.58, así que tres conductores del 12
                piden tubo de ¾" en THW y caben en ½" en THHN. Sale de la Tabla 5 del
                capítulo 10 de la NOM.
              </span>
            </label>
          </div>

          <div className="card">
            <Numero etiqueta="Holgura de obra" paso={0.05}
              valor={preferencias.holguraObra ?? 1.10}
              ayuda={'Multiplicador sobre los metros que salen del plano: cortes, desperdicio y ' +
                     'recorridos que el dibujo no ve. 1.10 = 10% extra. Es práctica de obra, no ' +
                     'norma — si tu cuadrilla desperdicia menos, bájalo.'}
              onGuardar={v => guardarPreferencia('holguraObra', v)} />
            <Numero etiqueta="Reserva del tablero" paso={0.05}
              valor={preferencias.reservaTablero ?? 0.25}
              ayuda={'Espacios libres que se dejan en el centro de carga, sobre los circuitos ' +
                     'calculados. 0.25 = 25% de reserva. La norma no la exige; sirve para que la ' +
                     'casa admita algo mañana sin cambiar tablero.'}
              onGuardar={v => guardarPreferencia('reservaTablero', v)} />
            <Numero etiqueta="Metros por circuito, cuando nadie midió" paso={1}
              valor={preferencias.metrosPorDefecto ?? 20}
              ayuda={'Longitud que se supone para un circuito sin distancia capturada. Es un ' +
                     'supuesto declarado: el sistema lo dice cuando lo usa. Bájalo si tus casas ' +
                     'son chicas, súbelo si trabajas residencias grandes.'}
              onGuardar={v => guardarPreferencia('metrosPorDefecto', v)} />
          </div>
        </>
      )}

      {seccion === 6 && (
        <PanelTaller taller={taller}
          onGuardar={t => store.guardar('config', { ...t, id: 'taller' })} />
      )}

      {/* ---- Herramientas ----
          El catálogo que alimenta la orden de servicio y la salida de bodega: cada
          producto declara las suyas (en la pestaña Productos) y aquí viven todas.
          Una herramienta no se borra —hay registros históricos que la nombran—:
          se desactiva, y deja de ofrecerse. */}
      {seccion === 7 && (
        <>
          <div className="nota">
            Cada producto declara sus herramientas al darse de alta (pestaña
            <b> Productos</b>). Con eso, la orden de servicio arma sola la lista de
            <b> herramientas a llevar</b> y la salida de bodega exige que regresen todas.
          </div>
          <AltaHerramienta onCrear={async nombre => {
            await store.guardar('catalogoHerramientas', { nombre, descripcion: '', activa: true })
          }} />
          {herramientas.length === 0 && (
            <div className="empty">
              <b>Todavía no hay herramientas registradas.</b>
              <p>Da de alta las del taller y la camioneta: taladro, megóhmetro, pinza
                 de corte… Con el nombre basta.</p>
            </div>
          )}
          {[...herramientas].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')).map(h => (
            <div className="card" key={h.id} style={{ opacity: h.activa === false ? 0.55 : 1 }}>
              <div className="grow">
                <Texto etiqueta="Nombre" valor={h.nombre}
                  onGuardar={v => v && store.guardar('catalogoHerramientas', { ...h, nombre: v })} />
                <Texto etiqueta="Descripción (marca, capacidad, dónde vive)" valor={h.descripcion}
                  onGuardar={v => store.guardar('catalogoHerramientas', { ...h, descripcion: v })} />
                <label className="f" style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                  <input type="checkbox" checked={h.activa !== false} style={{ width: 20, height: 20 }}
                    onChange={e => store.guardar('catalogoHerramientas', { ...h, activa: e.target.checked })} />
                  <span style={{ margin: 0 }}>Activa — se ofrece al dar de alta productos</span>
                </label>
              </div>
            </div>
          ))}
        </>
      )}

      {/* ---- Protocolos guiados: el contenido, editable como los factores ----
          El motor y el runner viven en código; QUÉ se pregunta, QUÉ se mide y con
          qué criterio es contenido de Juan — se edita aquí y la cuadrilla lo ve
          actualizado en la siguiente visita. Los umbrales numéricos se capturan
          con su fuente (norma o criterio propio), nunca vienen inventados. */}
      {seccion === 9 && (
        <>
          <div className="nota">
            Un protocolo es el <b>modo de trabajo correcto</b> hecho pasos: qué preguntar al
            llegar, qué medir con qué aparato, con qué criterio se decide, y qué decirle al
            cliente al cerrar. Se corre desde la <b>bitácora de la casa</b> y al sellarse
            queda como intervención — el registro prueba que el método se siguió.
          </div>
          <AltaProtocolo onCrear={async nombre => {
            const id = nombre.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
              .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60)
            await store.guardar('catalogoProtocolos', {
              id, nombre, cuando: '', herr: [], guionLlegada: [], pasos: [], guionCierre: []
            })
          }} />
          {protocolos.map(p => (
            <details className="card" key={p.id}>
              <summary style={{ cursor: 'pointer' }}>
                <span className="t">{p.nombre}</span>
                <span className="s" style={{ display: 'block' }}>
                  {(p.pasos || []).length} pasos · {(p.herr || []).length} herramientas
                </span>
              </summary>
              <EditorProtocolo p={p} herramientas={herramientas} />
            </details>
          ))}
        </>
      )}

      {/* ---- SYSCOM: el catálogo de referencia del "¿y no pones…?" ----
          Se llena con admin/sincronizar-syscom.mjs (feed de descarga, no el API).
          NO se suscribe en useDatos: son miles de documentos y solo se cargan al
          entrar aquí. Dar de alta copia el producto como insumo con el precio de
          LISTA público como costo — el margen lo ponen los factores, como siempre. */}
      {seccion === 8 && (
        <PanelSyscom dispositivos={dispositivos}
          onAdoptar={async p => {
            await store.guardar('catalogoDispositivos', {
              id: p.modelo, sku: p.modelo, nombre: p.nombre,
              costo: p.costoMxn, tipo: 'Complementos', iva: false,
              marca: p.marca, sat: p.sat, img: p.img || '',
              origen: 'syscom', sincronizadoEn: p.sincronizadoEn
            })
          }} />
      )}

      {/* La sesión no es una pestaña: vive al pie de Configuración, siempre visible,
          y solo cuando de verdad hay sesión (en modo exento de desarrollo no aparece).
          Al salir, la compuerta de main.jsx desmonta la app y vuelve a la puerta.
          En el módulo Catálogo no se repite: la sesión es asunto del negocio. */}
      {usuario() && grupo !== 'catalogo' && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="grow">
            <div className="t">Sesión iniciada</div>
            <div className="s">{usuario().email}</div>
          </div>
          <button type="button" className="ib" onClick={() => salir()}>Cerrar sesión</button>
        </div>
      )}
    </>
  )
}

/**
 * PANEL DE FABRICACIÓN — los despieces, la lista y el editor.
 *
 * Se comparte entre el módulo Catálogo (Juan) y la mesa del taller del aliado
 * (el papá): el despiece es conocimiento DEL FABRICANTE, y quien lo captura es
 * quien lo tiene. Vive exportado por eso.
 */
export function PanelFabricacion({ productos, despieces, cfgTaller, onGuardarDespiece }) {
  const [piezaAbierta, setPiezaAbierta] = useState(null)

  if (piezaAbierta) return (
    <>
      <button className="ib" style={{ marginBottom: 12 }}
        onClick={() => setPiezaAbierta(null)}>‹ Todas las piezas</button>
      <div className="t" style={{ margin: '0 4px 10px', fontSize: 18 }}>
        {productos.find(p => p.sku === piezaAbierta)?.nombre}
      </div>
      <Despiece
        sku={piezaAbierta}
        nombre={productos.find(p => p.sku === piezaAbierta)?.nombre}
        despiece={despieces[piezaAbierta]}
        cfgTaller={cfgTaller}
        onGuardar={d => onGuardarDespiece(piezaAbierta, d)} />
    </>
  )

  const taller = productos.filter(p => p.taller)
  const conD = taller.filter(p => tieneDespiece(despieces[p.sku]))
  const sinD = taller.filter(p => !tieneDespiece(despieces[p.sku]))
  const fila = p => {
    const d = despieces[p.sku]
    const r = tieneDespiece(d) ? calcularFabricacion(d, cfgTaller, 1) : null
    return (
      <div className="card tap" key={p.sku} onClick={() => setPiezaAbierta(p.sku)}>
        <div className="grow">
          <div className="t">{p.nombre}</div>
          <div className="s">
            {p.sku} · por {p.unidad === 'm2' ? 'm²' : p.unidad === 'ml' ? 'metro lineal' : 'pieza'}
            {r && ` · ${r.minutosMaquina} min de máquina`}
          </div>
        </div>
        {r
          ? <div className="money">{mx(r.costoTotal)}</div>
          : <div className="s">Sin despiece</div>}
        <div style={{ color: 'var(--gris)' }}>›</div>
      </div>
    )
  }
  return (
    <>
      <div className="warn">
        El despiece es de qué está hecha cada pieza: sus cortes, sus herrajes y sus
        procesos. De ahí salen solas las placas, los metros de corte, los minutos de
        máquina y el costo. <b>Tres piezas traen despiece de muestra</b> para que se vea
        el cálculo funcionando; las demás están en blanco esperando al taller.
      </div>
      {conD.length > 0 && <><div className="sec">Con despiece capturado</div>{conD.map(fila)}</>}
      <div className="sec">Pendientes de capturar — {sinD.length} piezas</div>
      {sinD.map(fila)}
    </>
  )
}

/**
 * PANEL DEL TALLER — lo que Juan pidió el 2026-08-15.
 *
 * *«Un interfaz donde yo introduzca los valores para más adelante irlos modificando según
 * la realidad, porque yo tampoco sé en este momento cómo hacerlo.»*
 *
 * Esa última frase es la que manda el diseño: **no se puede pedir precisión de entrada.**
 * Cada campo trae un valor de arranque que funciona, dice de dónde saldría el bueno, y se
 * corrige en un renglón. Una pantalla que exigiera los datos exactos antes de servir para
 * algo se quedaría vacía para siempre.
 *
 * El orden de los bloques es el del dinero, no el del catálogo: primero la placa —que es
 * el grueso del costo—, luego el minuto de máquina, luego el herraje. Y el
 * aprovechamiento va arriba de todo porque es el número que más gente ignora y el que más
 * mueve el resultado.
 */
export function PanelTaller ({ taller, onGuardar }) {
  const t = { ...TALLER_BASE, ...taller }

  const guardarRama = (rama, clave, campo, valor) => onGuardar({
    ...t,
    [rama]: {
      ...t[rama],
      [clave]: { ...t[rama][clave], [campo]: valor }
    }
  })

  return (
    <>
      <div className="nota">
        Todo lo de esta pantalla es <b>tuyo</b>: el precio al que TE venden la placa, lo que
        TE cuesta el minuto de máquina, lo que desperdicia TU corte. Ningún proveedor ni
        norma publica esto. Los valores que trae son de arranque, para que el cálculo
        funcione desde hoy — <b>se corrigen con la primera compra y el primer mueble real</b>,
        y a partir de ahí todas las cotizaciones se recalculan solas.
      </div>

      <div className="warn">
        <b>Sin calibrar.</b> Ninguno de estos números salió de tu taller todavía. Sirven para
        que el sistema entregue un orden de magnitud correcto, no para comprometer un precio
        con un cliente sin revisarlo.
      </div>

      {/* --- Aprovechamiento: el número que más mueve el costo y el que más se ignora --- */}
      <div className="sec">Aprovechamiento de la placa</div>
      <div className="card">
        <Numero etiqueta="Fracción de la placa que se vuelve pieza" paso={0.01}
          valor={t.aprovechamiento} provisional
          ayuda={'0.78 quiere decir que el 22% de cada placa se va a recorte. Es lo que ' +
                 'más mueve el costo de material y casi nadie lo mide: sale de pesar el ' +
                 'recorte de una semana contra las placas que entraron. Si tu anidado es ' +
                 'bueno, súbelo y todas las cotizaciones bajan.'}
          onGuardar={v => onGuardar({ ...t, aprovechamiento: v })} />
      </div>

      {/* --- Material: el grueso del costo --- */}
      <div className="sec">Placas y material</div>
      <div className="nota">
        El costo es de la <b>placa entera</b>, no del metro cuadrado, porque así se compra.
        El sistema redondea a placas completas y reporta el desperdicio.
      </div>
      {Object.entries(t.materiales).map(([k, m]) => (
        <div className="card" key={k}>
          <div className="t">{m.nombre}</div>
          <div className="s" style={{ marginBottom: 10 }}>
            {k} · {m.largo} × {m.ancho} mm
          </div>
          <Numero etiqueta="Costo de la placa entera" paso={10} valor={m.costo}
            onGuardar={v => guardarRama('materiales', k, 'costo', v)} />
        </div>
      ))}

      {/* --- Procesos: el recurso escaso --- */}
      <div className="sec">Minuto de máquina y de banco</div>
      <div className="nota">
        El costo por minuto incluye <b>máquina más operador</b>. Por eso el router cuesta
        más que el banco de armado: no es sólo el sueldo, es la máquina ocupada. La
        velocidad decide cuántos minutos tarda un corte, y sale de cronometrar una pieza
        real de principio a fin.
      </div>
      {Object.entries(t.procesos).map(([k, p]) => (
        <div className="card" key={k}>
          <div className="t">{p.nombre}</div>
          <div className="s" style={{ marginBottom: 10 }}>{k}</div>
          <div className="two">
            <Numero etiqueta="Costo por minuto" paso={0.5} valor={p.costoMinuto}
              onGuardar={v => guardarRama('procesos', k, 'costoMinuto', v)} />
            {p.metrosPorMinuto != null && (
              <Numero etiqueta="Metros por minuto" paso={0.1} valor={p.metrosPorMinuto}
                onGuardar={v => guardarRama('procesos', k, 'metrosPorMinuto', v)} />
            )}
            {p.porDoblez != null && (
              <Numero etiqueta="Minutos por doblez" paso={0.1} valor={p.porDoblez}
                onGuardar={v => guardarRama('procesos', k, 'porDoblez', v)} />
            )}
          </div>
        </div>
      ))}

      {/* --- Herrajes --- */}
      <div className="sec">Herrajes y consumibles</div>
      <div className="nota">
        Aquí es donde se va el dinero que nadie cuenta. Una cocina de diez muebles lleva
        veinte bisagras, ocho correderas y treinta metros de canto: sumados pesan más que
        una placa.
      </div>
      {Object.entries(t.herrajes).map(([k, h]) => (
        <div className="card" key={k}>
          <div className="t">{h.nombre}</div>
          <div className="s" style={{ marginBottom: 10 }}>{k} · por {h.unidad}</div>
          <Numero etiqueta={`Costo por ${h.unidad}`} paso={0.5} valor={h.costo}
            onGuardar={v => guardarRama('herrajes', k, 'costo', v)} />
        </div>
      ))}
    </>
  )
}

/**
 * PANEL SYSCOM — el catálogo de referencia, buscable, con alta en un toque.
 *
 * Carga las miles de fichas UNA vez al abrir la pestaña (nunca por suscripción
 * global) y filtra en memoria. El costo mostrado es el precio de LISTA público
 * convertido a MXN con el tipo de cambio del día de la sincronización — la fecha
 * se muestra porque un precio de hace un mes es un dato de hace un mes.
 */
function PanelSyscom({ dispositivos, onAdoptar }) {
  const [lista, setLista] = useState(null)
  const [filtro, setFiltro] = useState('')
  const [ocupado, setOcupado] = useState(null)
  useEffect(() => { store.listar('catalogoSyscom').then(setLista) }, [])

  if (lista === null) return <div className="empty">Cargando el catálogo de referencia…</div>
  if (!lista.length) return (
    <div className="empty">
      <b>El catálogo de referencia está vacío.</b>
      <p>Se llena desde la terminal: <code>node admin/sincronizar-syscom.mjs --marcas
         "HIKVISION,EPCOM" --aplicar</code> — ver el encabezado del script para el enlace
         del feed y el tipo de cambio.</p>
    </div>
  )

  const t = filtro.toLowerCase()
  const resultados = t
    ? lista.filter(p => [p.modelo, p.marca, p.nombre, p.cat2, p.cat3].join(' ').toLowerCase().includes(t))
    : []
  const visibles = resultados.slice(0, 50)
  const yaExiste = p => dispositivos.some(d => d.sku === p.modelo)
  const fecha = p => (p.sincronizadoEn || '').slice(0, 10)

  return (
    <>
      <div className="nota">
        Referencia de {lista.length.toLocaleString('es-MX')} productos SYSCOM. El costo es el
        precio de <b>lista público</b> en MXN (convertido al tipo de cambio del día de la
        sincronización) y <b>no incluye IVA</b>. «Dar de alta» lo copia a tus insumos; el
        margen lo siguen poniendo tus factores.
      </div>
      <div className="buscador">
        <input placeholder="Buscar por modelo, marca, nombre o categoría"
          aria-label="Buscar en el catálogo SYSCOM"
          value={filtro} onChange={e => setFiltro(e.target.value)} />
      </div>
      {!t && <div className="empty"><b>Escribe para buscar.</b><p>Modelo, marca, nombre o categoría — p. ej. «cámara bala», «DS-2CD», «UBIQUITI».</p></div>}
      {t && !resultados.length && <div className="empty"><b>Nada coincide con «{filtro}».</b></div>}
      {resultados.length > 50 && (
        <div className="s" style={{ margin: '0 4px 8px' }}>
          {resultados.length.toLocaleString('es-MX')} coincidencias — se muestran las primeras 50; afina la búsqueda.
        </div>
      )}
      {visibles.map(p => (
        <div className="card" key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {p.img
            ? <img className="ph xs" alt="" src={p.img} loading="lazy"
                onError={e => { e.target.style.visibility = 'hidden' }} />
            : <span className="ph xs" />}
          <div className="grow">
            <div className="t">{p.nombre}</div>
            <div className="s">
              {p.modelo} · {p.marca} · existencia {p.existencia} · sync {fecha(p)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="money">{mx(p.costoMxn)}<span className="s"> MXN</span></div>
            <div className="s">{p.precioUsd} USD × {p.tc}</div>
          </div>
          <button className="ib sm" disabled={yaExiste(p) || ocupado === p.id}
            onClick={async () => {
              setOcupado(p.id)
              try { await onAdoptar(p) } finally { setOcupado(null) }
            }}>
            {yaExiste(p) ? 'Ya es insumo' : ocupado === p.id ? 'Guardando…' : 'Dar de alta'}
          </button>
        </div>
      ))}
    </>
  )
}

/** Texto largo que guarda al salir — para instrucciones y criterios de protocolo. */
function TextoLargo({ etiqueta, valor, onGuardar, alto = 70 }) {
  return (
    <label className="f">
      <span>{etiqueta}</span>
      <textarea defaultValue={valor || ''} style={{ minHeight: alto }}
        onBlur={e => { const v = e.target.value.trim(); if (v !== (valor || '')) onGuardar(v) }} />
    </label>
  )
}

/** Renglón de alta de un protocolo nuevo: con el nombre basta, el resto se edita. */
function AltaProtocolo({ onCrear }) {
  const [nombre, setNombre] = useState('')
  return (
    <div className="card" style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
      <label className="f grow" style={{ margin: 0 }}>
        <span>Protocolo nuevo</span>
        <input value={nombre} placeholder="P. ej. «Apagador inteligente que no responde»"
          onChange={e => setNombre(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && nombre.trim()) { onCrear(nombre.trim()); setNombre('') } }} />
      </label>
      <button className="ib pri" disabled={!nombre.trim()}
        onClick={() => { onCrear(nombre.trim()); setNombre('') }}>Crear</button>
    </div>
  )
}

/** El editor de un protocolo: sus guiones, sus herramientas y sus pasos. */
function EditorProtocolo({ p, herramientas }) {
  const guardar = campos => store.guardar('catalogoProtocolos', { ...p, ...campos })
  const mutarPaso = (i, campos) =>
    guardar({ pasos: p.pasos.map((x, j) => (j === i ? { ...x, ...campos } : x)) })
  const aLineas = arr => (arr || []).join('\n')
  const deLineas = texto => texto.split('\n').map(l => l.trim()).filter(Boolean)

  return (
    <div style={{ marginTop: 12 }}>
      <Texto etiqueta="Nombre" valor={p.nombre}
        onGuardar={v => v && guardar({ nombre: v })} />
      <TextoLargo etiqueta="Cuándo se usa" valor={p.cuando}
        onGuardar={v => guardar({ cuando: v })} />
      <SelectorHerramientas seleccion={p.herr || []} herramientas={herramientas}
        onCambiar={ids => guardar({ herr: ids })} />
      <TextoLargo etiqueta="Guion de llegada — una pregunta por renglón"
        valor={aLineas(p.guionLlegada)}
        onGuardar={v => guardar({ guionLlegada: deLineas(v) })} />

      <div className="sec">Pasos</div>
      {(p.pasos || []).map((paso, i) => (
        <div className="card" key={paso.id || i}>
          <div className="grow">
            <Texto etiqueta={`Paso ${i + 1} — título`} valor={paso.titulo}
              onGuardar={v => mutarPaso(i, { titulo: v })} />
            <TextoLargo etiqueta="Instrucción (qué se hace y con qué)" valor={paso.instruccion}
              onGuardar={v => mutarPaso(i, { instruccion: v })} />
            <div className="two">
              <label className="f"><span>Tipo</span>
                <select defaultValue={paso.tipo || 'verificacion'}
                  onChange={e => mutarPaso(i, { tipo: e.target.value })}>
                  <option value="medicion">Medición — exige capturar un valor</option>
                  <option value="verificacion">Verificación — cumple / no cumple</option>
                </select>
              </label>
              <Texto etiqueta="Unidad (si es medición)" valor={paso.unidad}
                onGuardar={v => mutarPaso(i, { unidad: v })} />
            </div>
            <TextoLargo etiqueta="Criterio de decisión (umbral CON SU FUENTE: norma o criterio propio)"
              valor={paso.criterio} onGuardar={v => mutarPaso(i, { criterio: v })} />
            <button className="ib sm" onClick={() => {
              guardar({ pasos: p.pasos.filter((_, j) => j !== i) })
            }}>Quitar este paso</button>
          </div>
        </div>
      ))}
      <button className="ib" onClick={() => guardar({
        pasos: [...(p.pasos || []), {
          id: 'paso-' + Date.now().toString(36),
          titulo: '', instruccion: '', tipo: 'verificacion', unidad: '', criterio: ''
        }]
      })}>Agregar un paso</button>

      <TextoLargo etiqueta="Guion de cierre — un punto por renglón"
        valor={aLineas(p.guionCierre)}
        onGuardar={v => guardar({ guionCierre: deLineas(v) })} />
    </div>
  )
}

/** Texto corto de para qué sirve técnicamente un insumo, para la etiqueta «automático». */
function descripcionTec (t) {
  if (t.clase === 'cable') return `cable ${t.awg} AWG ${String(t.aislamiento).toUpperCase()}`
  if (t.clase === 'conduit') return `tubo conduit ${t.pulgadas}`
  if (t.clase === 'tubo-hidraulico') return `tubo hidráulico ${t.pulgadas}`
  if (t.clase === 'codo') return `codo ${t.pulgadas}`
  if (t.clase === 'te') return `te ${t.pulgadas}`
  if (t.clase === 'boquilla') return `boquilla ${t.boquilla}`
  if (t.clase === 'cuerpo-aspersor') return 'cuerpo de aspersor'
  if (t.clase === 'bomba') return `bomba ${t.bomba}`
  if (t.clase === 'proteccion') return `interruptor ${t.amperes} A · ${t.polos}P`
  if (t.clase === 'valvula-zona') return 'válvula de zona'
  if (t.clase === 'programador') return `programador ${t.estaciones} estaciones`
  return t.clase
}
